import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, Typography, Grid, Paper, IconButton, Dialog, DialogContent, 
  Button, Tooltip, Checkbox, Toolbar, Chip, TextField, DialogActions, 
  DialogTitle, RadioGroup, Radio, FormControlLabel, FormControl, FormLabel,
  Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import EditIcon from '@mui/icons-material/Edit';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearIcon from '@mui/icons-material/Clear';
import CategoryIcon from '@mui/icons-material/Category';
import LabelIcon from '@mui/icons-material/Label';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CloseIcon from '@mui/icons-material/Close';
import { useImage } from '../../hooks/useImage';

/**
 * Project Image Gallery Component
 * Displays all images associated with a project
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - ID of the project to get images for
 * @param {boolean} props.loadOnMount - Whether to load images on component mount (default: true)
 * @param {boolean} props.allowDelete - Whether to allow image deletion (default: false)
 * @param {Function} props.onImageClick - Optional callback when an image is clicked
 * @param {Function} props.onImageSelect - Optional callback when an image is selected
 * @param {boolean} props.selectable - Whether images are selectable (default: false)
 * @param {boolean} props.multiSelectable - Whether multiple images can be selected (default: false)
 * @param {Function} props.onMultiSelect - Callback when multiple images are selected
 * @param {Function} props.onEditImage - Optional callback when an image is selected for editing
 * @param {Array} props.images - Optional array of images to display instead of loading from server
 */
const ProjectImageGallery = ({ 
  projectId, 
  loadOnMount = true,
  allowDelete = false,
  onImageClick,
  onImageSelect,
  onEditImage,
  selectable = false,
  multiSelectable = false,
  onMultiSelect,
  images
}) => {
  const { loading, projectImages: loadedImages, getProjectImages, deleteImage, updateImage, imageCategories, getImageCategories } = useImage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  
  // Use external images array if provided, otherwise use images from the hook
  const projectImages = images || loadedImages;
  
  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  
  // Function to load images from the server
  const loadImages = async () => {
    if (!projectId) return;
    try {
      await getProjectImages(projectId);
    } catch (error) {
      console.error('Failed to load project images:', error);
    }
  };

  useEffect(() => {
    if (loadOnMount && projectId && !images) {
      // Only load images from server if external images weren't provided
      loadImages();
      // Load available categories for this project
      if (projectId) {
        getImageCategories(projectId);
      }
    }
  }, [projectId, loadOnMount, images]);

  // When multiSelectable changes, reset selection state
  useEffect(() => {
    if (!multiSelectable) {
      setSelectedImages([]);
      setSelectionMode(false);
    }
  }, [multiSelectable]);
  
  // Notify parent component when selection changes
  useEffect(() => {
    if (onMultiSelect && selectedImages.length > 0) {
      const selected = projectImages.filter(img => selectedImages.includes(img._id));
      onMultiSelect(selected);
    }
  }, [selectedImages, onMultiSelect, projectImages]);
  
  // Category dialog handlers
  const handleOpenCategoryDialog = useCallback(() => {
    // Get the current category of the first selected image as default
    if (selectedImages.length > 0) {
      const firstImage = projectImages.find(img => img._id === selectedImages[0]);
      if (firstImage && firstImage.category) {
        setSelectedCategory(firstImage.category);
      } else {
        setSelectedCategory('uncategorized');
      }
    }
    setCategoryDialogOpen(true);
  }, [selectedImages, projectImages]);

  const handleCloseCategoryDialog = useCallback(() => {
    setCategoryDialogOpen(false);
  }, []);

  const handleCategoryChange = useCallback((event) => {
    setSelectedCategory(event.target.value);
  }, []);

  const handleApplyCategory = useCallback(async () => {
    if (selectedImages.length === 0 || !selectedCategory) {
      handleCloseCategoryDialog();
      return;
    }
    
    try {
      // Update each selected image with the new category
      for (const imageId of selectedImages) {
        await updateImage(imageId, { category: selectedCategory });
      }
      
      // Refresh images to show updated categories
      await loadImages();
      handleCloseCategoryDialog();
    } catch (error) {
      console.error('Failed to update image categories:', error);
    }
  }, [selectedImages, selectedCategory, updateImage, loadImages]);
  
  // Tag dialog handlers
  const handleOpenTagDialog = useCallback(() => {
    // Get existing tags from selected images to pre-populate
    if (selectedImages.length > 0) {
      const allTags = new Set();
      
      // Collect tags from all selected images
      selectedImages.forEach(imageId => {
        const image = projectImages.find(img => img._id === imageId);
        if (image && image.tags && Array.isArray(image.tags)) {
          image.tags.forEach(tag => allTags.add(tag));
        }
      });
      
      setSelectedTags(Array.from(allTags));
    } else {
      setSelectedTags([]);
    }
    
    setTagInput('');
    setTagDialogOpen(true);
  }, [selectedImages, projectImages]);

  const handleCloseTagDialog = useCallback(() => {
    setTagDialogOpen(false);
  }, []);

  const handleAddTag = useCallback(() => {
    if (!tagInput.trim()) return;
    
    const newTag = tagInput.trim().toLowerCase();
    if (!selectedTags.includes(newTag)) {
      setSelectedTags([...selectedTags, newTag]);
    }
    
    setTagInput('');
  }, [tagInput, selectedTags]);

  const handleRemoveTag = useCallback((tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  }, [selectedTags]);

  const handleApplyTags = useCallback(async () => {
    if (selectedImages.length === 0) {
      handleCloseTagDialog();
      return;
    }
    
    try {
      // Update each selected image with the tags
      for (const imageId of selectedImages) {
        await updateImage(imageId, { tags: selectedTags });
      }
      
      // Refresh images to show updated tags
      await loadImages();
      handleCloseTagDialog();
    } catch (error) {
      console.error('Failed to update image tags:', error);
    }
  }, [selectedImages, selectedTags, updateImage, loadImages]);
  
  const handleImageClick = (image) => {
    if (selectionMode) {
      toggleImageSelection(image._id);
    } else if (onImageClick) {
      onImageClick(image);
    } else {
      setSelectedImage(image);
      setLightboxOpen(true);
    }
  };
  
  const handleSelect = (image) => {
    if (onImageSelect) {
      onImageSelect(image);
    }
  };

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode(prev => !prev);
    if (selectionMode) {
      setSelectedImages([]);
    }
  }, [selectionMode]);

  const toggleImageSelection = useCallback((imageId) => {
    setSelectedImages(prev => {
      if (prev.includes(imageId)) {
        return prev.filter(id => id !== imageId);
      } else {
        return [...prev, imageId];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedImages.length === projectImages.length) {
      // If all are selected, deselect all
      setSelectedImages([]);
    } else {
      // Otherwise, select all
      setSelectedImages(projectImages.map(img => img._id));
    }
  }, [projectImages, selectedImages]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedImages.length === 0) return;
    
    const confirmed = window.confirm(`Delete ${selectedImages.length} selected images?`);
    if (!confirmed) return;
    
    try {
      // Delete each selected image
      for (const imageId of selectedImages) {
        await deleteImage(imageId);
      }
      
      // Clear selection
      setSelectedImages([]);
    } catch (error) {
      console.error('Failed to delete selected images:', error);
    }
  }, [selectedImages, deleteImage]);

  const handleMarkAsFavorite = useCallback(async (favorite) => {
    if (selectedImages.length === 0) return;
    
    try {
      // Update each selected image
      for (const imageId of selectedImages) {
        await updateImage(imageId, { isFavorite: favorite });
      }
      
      // Refresh images to show updated favorite status
      await loadImages();
    } catch (error) {
      console.error('Failed to update selected images:', error);
    }
  }, [selectedImages, updateImage, loadImages]);
  
  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };
  
  const handleDownload = async (image) => {
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `image-${image._id}.png`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  };

  if (loading && projectImages.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Loading project images...</Typography>
      </Box>
    );
  }

  if (projectImages.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>No images found for this project</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Selection toolbar - only visible when multiSelectable is true */}
      {multiSelectable && (
        <Toolbar 
          variant="dense" 
          sx={{ 
            mb: 2, 
            borderRadius: 1, 
            bgcolor: 'background.paper',
            boxShadow: 1,
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={selectionMode ? <ClearIcon /> : <SelectAllIcon />}
              onClick={toggleSelectionMode}
              color={selectionMode ? "error" : "primary"}
              variant={selectionMode ? "contained" : "outlined"}
              size="small"
              sx={{ mr: 1 }}
            >
              {selectionMode ? "Cancel Selection" : "Select Images"}
            </Button>
            
            {selectionMode && (
              <Button
                startIcon={<SelectAllIcon />}
                onClick={handleSelectAll}
                color="primary"
                variant="outlined"
                size="small"
              >
                {selectedImages.length === projectImages.length ? "Deselect All" : "Select All"}
              </Button>
            )}
          </Box>
          
          {selectionMode && selectedImages.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={`${selectedImages.length} selected`} 
                color="primary" 
                size="small" 
              />
              
              <Button
                startIcon={<FavoriteIcon />}
                onClick={() => handleMarkAsFavorite(true)}
                color="secondary"
                variant="outlined"
                size="small"
              >
                Add to Favorites
              </Button>
              
              <Button
                startIcon={<CategoryIcon />}
                color="primary"
                variant="outlined"
                size="small"
                onClick={handleOpenCategoryDialog}
              >
                Categorize
              </Button>
              
              <Button
                startIcon={<LabelIcon />}
                color="primary"
                variant="outlined"
                size="small"
                onClick={handleOpenTagDialog}
              >
                Tag
              </Button>
              
              {allowDelete && (
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                  color="error"
                  variant="outlined"
                  size="small"
                >
                  Delete
                </Button>
              )}
            </Box>
          )}
        </Toolbar>
      )}
      
      <Grid container spacing={2}>
        {projectImages.map((image) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={image._id}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 1, 
                position: 'relative',
                cursor: 'pointer',
                transition: 'transform 0.2s, border 0.2s',
                '&:hover': {
                  transform: 'scale(1.02)'
                },
                ...(selectedImages.includes(image._id) && {
                  border: '2px solid #1976d2',
                  boxShadow: '0 0 5px rgba(25, 118, 210, 0.5)'
                })
              }}
              onClick={() => handleImageClick(image)}
            >
              {/* Selection checkbox (only visible in selection mode) */}
              {selectionMode && (
                <Checkbox
                  checked={selectedImages.includes(image._id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleImageSelection(image._id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    zIndex: 10,
                    bgcolor: 'rgba(255,255,255,0.7)',
                    borderRadius: '4px'
                  }}
                />
              )}
              
              {/* Favorite indicator (if image is favorited) */}
              {image.isFavorite && (
                <Tooltip title="Favorite">
                  <FavoriteIcon 
                    color="error" 
                    sx={{ 
                      position: 'absolute', 
                      top: 8, 
                      right: 8, 
                      zIndex: 1,
                      filter: 'drop-shadow(0px 0px 1px rgba(0,0,0,0.5))'
                    }} 
                  />
                </Tooltip>
              )}
              
              {/* Category indicator (if image has a category) */}
              {image.category && image.category !== 'uncategorized' && (
                <Chip
                  label={image.category}
                  size="small"
                  sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    left: 8, 
                    zIndex: 1,
                    opacity: 0.8,
                    fontSize: '0.6rem',
                    height: '20px'
                  }}
                />
              )}
              
              <img 
                src={image.url} 
                alt={`Project image ${image._id}`}
                style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }}
              />
              
              {/* Action buttons */}
              <Box sx={{ 
                position: 'absolute', 
                top: image.isFavorite ? 40 : 8, 
                right: 8, 
                display: 'flex',
                gap: '4px',
                opacity: 0.7,
                '&:hover': { opacity: 1 }
              }}>
                <Tooltip title="View Full Size">
                  <IconButton 
                    size="small" 
                    sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!selectionMode) {
                        setSelectedImage(image);
                        setLightboxOpen(true);
                      }
                    }}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Download Image">
                  <IconButton 
                    size="small" 
                    sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(image);
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                {onEditImage && (
                  <Tooltip title="Edit Image">
                    <IconButton 
                      size="small" 
                      sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditImage(image.url);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                
                {allowDelete && (
                  <Tooltip title="Delete Image">
                    <IconButton 
                      size="small" 
                      sx={{ bgcolor: 'rgba(255,0,0,0.6)', color: 'white' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const confirmed = window.confirm('Are you sure you want to delete this image?');
                        if (confirmed) {
                          deleteImage(image._id);
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              
              {/* Single select button (only shown if selectable and not in selection mode) */}
              {selectable && !selectionMode && (
                <Box sx={{ mt: 1 }}>
                  <Button 
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(image);
                    }}
                  >
                    Select
                  </Button>
                </Box>
              )}
              
              {/* Image metadata */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {new Date(image.createdAt).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {image.prompt}
                </Typography>
                {image.tags && image.tags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {image.tags.slice(0, 3).map(tag => (
                      <Chip 
                        key={tag} 
                        label={tag} 
                        size="small" 
                        sx={{ height: '16px', fontSize: '0.6rem' }} 
                      />
                    ))}
                    {image.tags.length > 3 && (
                      <Chip 
                        label={`+${image.tags.length - 3}`} 
                        size="small" 
                        sx={{ height: '16px', fontSize: '0.6rem' }} 
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      {/* Image Lightbox */}
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 1, textAlign: 'center', bgcolor: 'black' }}>
          {selectedImage && (
            <>
              <img 
                src={selectedImage.url} 
                alt={`Full size ${selectedImage._id}`}
                style={{ maxWidth: '100%', maxHeight: '80vh' }}
              />
              <Box sx={{ mt: 2, color: 'white' }}>
                <Typography>{selectedImage.prompt}</Typography>
                <Typography variant="caption">
                  Generated on {new Date(selectedImage.createdAt).toLocaleString()}
                </Typography>
                
                {selectedImage.category && selectedImage.category !== 'uncategorized' && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Category: <Chip label={selectedImage.category} size="small" />
                  </Typography>
                )}
                
                {selectedImage.tags && selectedImage.tags.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Tags:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, justifyContent: 'center' }}>
                      {selectedImage.tags.map(tag => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(selectedImage)}
                  >
                    Download
                  </Button>
                  
                  <Button
                    variant="contained"
                    color={selectedImage.isFavorite ? "error" : "secondary"}
                    startIcon={<FavoriteIcon />}
                    onClick={async () => {
                      await updateImage(selectedImage._id, { isFavorite: !selectedImage.isFavorite });
                      // Close and refresh
                      handleCloseLightbox();
                      loadImages();
                    }}
                  >
                    {selectedImage.isFavorite ? "Remove from Favorites" : "Add to Favorites"}
                  </Button>
                  
                  {onEditImage && (
                    <Button 
                      variant="contained" 
                      color="info"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        handleCloseLightbox();
                        onEditImage(selectedImage.url);
                      }}
                    >
                      Edit Image
                    </Button>
                  )}
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Category Dialog */}
      <Dialog 
        open={categoryDialogOpen} 
        onClose={handleCloseCategoryDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Set Image Category
          <IconButton
            aria-label="close"
            onClick={handleCloseCategoryDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Categorize {selectedImages.length} selected image{selectedImages.length !== 1 ? 's' : ''}
            </Typography>
            
            <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
              <FormLabel component="legend">Category</FormLabel>
              <RadioGroup
                aria-label="category"
                name="category-radio-buttons-group"
                value={selectedCategory}
                onChange={handleCategoryChange}
              >
                <FormControlLabel value="uncategorized" control={<Radio />} label="Uncategorized" />
                <FormControlLabel value="logo" control={<Radio />} label="Logo" />
                <FormControlLabel value="icon" control={<Radio />} label="Icon" />
                <FormControlLabel value="wireframe" control={<Radio />} label="Wireframe" />
                <FormControlLabel value="screenshot" control={<Radio />} label="Screenshot" />
                <FormControlLabel value="background" control={<Radio />} label="Background" />
                <FormControlLabel value="user-interface" control={<Radio />} label="User Interface" />
                <FormControlLabel value="design" control={<Radio />} label="Design" />
                <FormControlLabel value="other" control={<Radio />} label="Other" />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCategoryDialog}>Cancel</Button>
          <Button onClick={handleApplyCategory} variant="contained" color="primary">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Tag Dialog */}
      <Dialog 
        open={tagDialogOpen} 
        onClose={handleCloseTagDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Tags
          <IconButton
            aria-label="close"
            onClick={handleCloseTagDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Add tags to {selectedImages.length} selected image{selectedImages.length !== 1 ? 's' : ''}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 1 }}>
              <TextField
                label="New Tag"
                variant="outlined"
                size="small"
                fullWidth
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleAddTag}
                disabled={!tagInput.trim()}
              >
                Add
              </Button>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Tags:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {selectedTags.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No tags added yet
                  </Typography>
                ) : (
                  selectedTags.map(tag => (
                    <Chip 
                      key={tag} 
                      label={tag} 
                      onDelete={() => handleRemoveTag(tag)} 
                      size="small" 
                    />
                  ))
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTagDialog}>Cancel</Button>
          <Button onClick={handleApplyTags} variant="contained" color="primary">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectImageGallery;